/**
 * FieldError Component
 * ====================
 * Displays validation error message for a single form field.
 * Integrates seamlessly with the validation system.
 */

import React from 'react';
import clsx from 'clsx';
import { PiWarningCircleLight } from 'react-icons/pi';

interface FieldErrorProps {
  error?: string;
  visible?: boolean;
  className?: string;
  showIcon?: boolean;
}

/**
 * FieldError: Displays a single field's validation error
 */
const FieldError: React.FC<FieldErrorProps> = ({
  error,
  visible = true,
  className,
  showIcon = true,
}) => {
  if (!error || !visible) {
    return null;
  }

  return (
    <div
      className={clsx(
        // The canonical error row — see FORM_SPEC.md. One tone (danger-500) in
        // both themes, one icon, on the same 1.5 step as every other field row.
        'flex items-start gap-1.5 mt-1.5 text-xs',
        'text-danger-500',
        className
      )}
      role="alert"
      aria-live="polite"
    >
      {showIcon && (
        <PiWarningCircleLight
          className="w-4 h-4 flex-shrink-0 text-danger-500"
          aria-hidden="true"
        />
      )}
      <span className="flex-1">{error}</span>
    </div>
  );
};

export default FieldError;
